/**
 * Centralized business information — the SINGLE source of truth for all
 * publicly-visible RCM Biosciences contact / branding / hours data.
 *
 * Rule: NEVER hard-code the business name, email, phone number, address,
 * hours, website URL, or research-use disclaimer inside individual pages
 * or components. Import from here instead. That way, when Legal changes
 * the disclaimer wording or when we move ZIP codes, we edit one file
 * and every page updates coherently (footer, contact, checkout, About,
 * email templates, Organization/LocalBusiness schemas, translated pages,
 * etc.).
 *
 * Do NOT put passwords, API keys, or payment credentials in this file —
 * this is FRONTEND config that ships to the browser bundle. Secrets live
 * in backend `.env` only.
 */
export const businessConfig = {
  legalName: 'RCM Biosciences',
  brandName: 'RCM Biosciences',
  displayName: 'RCM Biosciences',
  website: 'https://rcmbiosciences.com',
  email: 'research@rcmbiosciences.com',
  // No public support phone numbers (owner request). Apple Cash recipient details live only in
  // the private post-order payment instructions (translations checkout.paymentDetails / backend).
  location: {
    street: '346 William Montgomery Rd',
    city: 'Livingston',
    state: 'Texas',
    stateAbbr: 'TX',
    postalCode: '77351',
    country: 'United States',
    countryCode: 'US',
  },
  hours: {
    timezone: 'America/Chicago',
    // Human-readable strings (English). ES translations live in the
    // language files and reference these keys.
    mondayFriday: '9:00 AM – 5:00 PM Central Time',
    saturday: 'Closed',
    sunday: 'Closed',
    // Machine-readable OpeningHoursSpecification for schema.org JSON-LD.
    schema: [
      {
        '@type': 'OpeningHoursSpecification',
        dayOfWeek: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'],
        opens: '09:00',
        closes: '17:00',
      },
    ],
  },
  // Uniform "research use only" disclaimer used at checkout, on every
  // product page, in emails, and in the footer.
  disclaimer:
    'For research use only. Not for human consumption or diagnostic, therapeutic, or clinical use.',
  // Marketing constants — reward amounts stored as raw NUMBERS so callers
  // can format them however they need (USD, other currencies later, or
  // interpolate into translation strings via the `{amount}` placeholder
  // system). Never hard-code the formatted string in a translation file
  // or a component — always resolve through `formatUsd()` below.
  rewards: {
    referralCredit: 20,        // $20 credit per successful referral
    partnerCommission: 35,     // $35 payout per new-customer sign-up
    partnerMinimumOrder: 150,  // $150 minimum first order to qualify commission
  },
};

// USD formatter shared by every page that displays a reward / price.
// - Integer amounts render as "$35" (no cents), matching marketing copy.
// - Non-integer amounts render as "$53.34" (two decimals).
// Accepts number OR numeric string; returns "" for null/undefined so a
// misconfigured value doesn't render literal "undefined" in the UI.
export const formatUsd = (amount) => {
  if (amount === null || amount === undefined || amount === '') return '';
  const n = typeof amount === 'number' ? amount : Number(amount);
  if (!Number.isFinite(n)) return '';
  return Number.isInteger(n) ? `$${n}` : `$${n.toFixed(2)}`;
};

// Convenience getters for common templated strings so callers stay readable.
export const businessCityState = () =>
  `${businessConfig.location.city}, ${businessConfig.location.stateAbbr}`;

export const businessCityStateCountry = () =>
  `${businessConfig.location.city}, ${businessConfig.location.stateAbbr}, ${businessConfig.location.country}`;

export default businessConfig;
